import * as yup from 'yup';
import useFormStore from '../../features/Registration/useFormStore';
import { isUserSMB } from '../isUserSMB';
import { sizeOfPortfolio } from '../../features/Registration/constants/UnitOptions';

// Common validation rules
const firstNameValidation = yup
  .string()
  .max(100, 'First name can only have 100 characters')
  .required('First name is required')
  .trim();

const lastNameValidation = yup
  .string()
  .max(100, 'Last name can only have 100 characters')
  .required('Last name is required')
  .trim();

const phoneNumberValidation = yup
  .string()
  .optional()
  .test(
    'valid-number',
    'Enter a valid number',
    (value?: string) => value === '' || value == undefined || /^\(\d{3}\) \d{3}-\d{4}$/.test(value),
  );

const phoneNumberDigitsValidation = yup
  .string()
  .optional()
  .test(
    'valid-number',
    'Enter a valid number',
    (value?: string) => value === '' || value == undefined || /^\d{10}$/.test(value),
  );

// Phone number validation for single page signup:
// - Kill switch OFF + not TransUnion → phone field hidden, validation skipped (optional)
// - Kill switch ON or TransUnion, phone field visible:
//   - Required when: (isTransunionReferral OR isSmbPhoneMandatory) AND not institutional
//   - Optional with format validation otherwise
const phoneNumberRequiredForTransunion = yup
  .string()
  .when(
    ['$isTransunionReferral', '$isInstitutionalUser', '$isSmbPhoneMandatory', '$isSmbPhoneEnabled'],
    {
      is: (
        isTransunionReferral: boolean,
        isInstitutionalUser: boolean,
        isSmbPhoneMandatory: boolean,
        isSmbPhoneEnabled: boolean,
      ) => {
        // Phone field is hidden when: institutional user, or kill switch OFF and not TransUnion
        const isPhoneHidden = isInstitutionalUser || (!isSmbPhoneEnabled && !isTransunionReferral);
        if (isPhoneHidden) return false;
        // Phone field is visible → check if required
        return isTransunionReferral || isSmbPhoneMandatory;
      },
      then: (schema) =>
        schema
          .required('Phone number is required')
          .test('valid-number', 'Enter a valid number', (value?: string) =>
            /^\d{10}$/.test(value ?? ''),
          ),
      otherwise: (schema) =>
        schema
          .optional()
          .test(
            'valid-number',
            'Enter a valid number',
            (value?: string) => value === '' || value == undefined || /^\d{10}$/.test(value),
          ),
    },
  );

const stateValidation = yup
  .object({
    id: yup.number().required(),
    label: yup.string().required(),
    value: yup.string().required(),
  })
  .required('State is required')
  .test(
    'isNotAlaska',
    "Sorry, we don't support properties in Alaska. Please change your selection if you have properties in another state",
    (state) => state?.value?.toLowerCase() !== 'ak',
  );

// State validation that is optional when isTransunionReferral is true
const stateValidationForTransunion = yup
  .object({
    id: yup.number().required(),
    label: yup.string().required(),
    value: yup.string().required(),
  })
  .when('$isTransunionReferral', {
    is: true,
    then: (schema) => schema.optional().default(undefined),
    otherwise: (schema) =>
      schema
        .required('State is required')
        .test(
          'isNotAlaska',
          "Sorry, we don't support properties in Alaska. Please change your selection if you have properties in another state",
          (state) => (state as { value?: string } | undefined)?.value?.toLowerCase() !== 'ak',
        ),
  });

const emailValidation = yup
  .string()
  .required('Email is required')
  .email('Not a valid email')
  .trim();

const propertyLLCNotRequired = yup.string().optional().max(100, 'LLC can only have 100 characters');

const sizeOfPortfolioValidation = yup
  .string()
  .required('Size of portfolio is required')
  .oneOf(Object.values(sizeOfPortfolio));

// Size of portfolio validation that is optional when isTransunionReferral is true (pre-filled with default)
const sizeOfPortfolioValidationForTransunion = yup
  .mixed<sizeOfPortfolio>()
  .oneOf(Object.values(sizeOfPortfolio) as sizeOfPortfolio[])
  .when('$isTransunionReferral', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required('Size of portfolio is required'),
  });

const roleValidator = yup.string().required('User role is required');

const companyValidator = yup
  .string()
  .max(100, 'Company can only have 100 characters')
  .required('Company is required')
  .trim();

const companyValidatorByPortfolio = yup.string().when('sizeOfPortfolio', {
  is: (v: sizeOfPortfolio) => !isUserSMB(v),
  then: (s) =>
    s.max(100, 'Company can only have 100 characters').required('Company is required').trim(),
  otherwise: (s) => s.optional().trim(),
});

// customized validation rules
const propertyLLCValidationWithUnits = yup.string().when([], {
  is: () => {
    return !isUserSMB(useFormStore.getState().units?.sizeOfPortfolio);
  },
  then: (schema) =>
    schema
      .max(100, 'Company name can only have 100 characters')
      .required('Company name is required'),
  otherwise: (schema) => schema.optional(),
});

const companyNameValidator = yup
  .string()
  .max(100, 'Company name can only have 100 characters')
  .optional()
  .trim();

const unitsOwnedValidation = yup.number().when('sizeOfPortfolio', {
  is: (val: string) => val == sizeOfPortfolio.oneToFifty,
  then: (schema) =>
    schema
      .typeError('You must specify a number')
      .required('Number of units is required')
      .test(
        'numUnder50',
        'Number of units must be between 1 and 50',
        (val: number) => val > 0 && +val <= 50,
      ),
  otherwise: (schema) => schema.notRequired().default(undefined),
});

// Schemas
export const brokerSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  propertyLLC: companyValidator,
  email: emailValidation,
  phoneNumber: phoneNumberValidation,
  state: stateValidation,
});

export const profileSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  phone: phoneNumberDigitsValidation,
  propertyLLC: propertyLLCNotRequired,
  email: emailValidation,
});

export const unitsOwnedSchema = yup.object().shape({
  sizeOfPortfolio: sizeOfPortfolioValidation,
  email: emailValidation,
});

export const registerAndAgreeSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  propertyLLC: propertyLLCValidationWithUnits,
  companyName: companyNameValidator,
  phoneNumber: phoneNumberValidation,
  state: stateValidation,
});

export const singlePageSignupSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  email: emailValidation,
  sizeOfPortfolio: sizeOfPortfolioValidationForTransunion, // optional for TransUnion referral (pre-filled)
  state: stateValidationForTransunion, // optional for TransUnion referral
  propertyLLC: companyValidatorByPortfolio, // only for institutional
  phoneNumber: phoneNumberRequiredForTransunion, // required for TransUnion referral (when not institutional)
});

export const UserInvitesSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  email: emailValidation,
  role: roleValidator,
});

export const UnitsOwnedOnlySchema = yup.object().shape({
  sizeOfPortfolio: sizeOfPortfolioValidation,
  totalUnitsOwned: unitsOwnedValidation,
});

export const UserEditSchema = yup.object().shape({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  email: emailValidation,
  role: roleValidator,
});
